// Bottom Navigation (mobile-shell, скрыт на lg:+ — синхронно с порогом
// переключения на desktop-sidebar). Тактильная навигация-пульт: активный
// пункт — приподнятый чип с верхним акцентом, тач-таргеты ≥ 44px, safe-area
// снизу. Дизайн-язык «Картотека» §8.A.
//
// 5 слотов: Главная · Обучение · «＋» · Библиотека · «Ещё».
// Первые два и Библиотека — из BOTTOM_NAV (раскладка slice(0,2) → «＋» →
// slice(2) → «Ещё»); центральная синяя кнопка «＋» открывает попап «что
// добавить» (загрузить материал / записать лекцию), «Ещё» — drawer со всеми
// разделами. Без «Ещё» 9+ фич (Тесты/Карточки/Записи/...) были бы на mobile
// достижимы только через бургер — паритет с desktop-sidebar нарушался.
//
// «Чат» из нижней навигации убран: репетитор — глобальная боковая панель
// (TutorSidePanel), она доступна с любого экрана и слот не занимает.
// См. docs/plans/LEARNING-SYSTEM-UI.md §8.
import { useHaptic } from "@studyhub/platform/hooks";
import { IconLayoutGrid, IconPlus } from "@tabler/icons-react";
import { Link, useRouterState } from "@tanstack/react-router";
import { forwardRef } from "react";
import { useTranslation } from "react-i18next";
import { isDimmedPresentation, ModuleNavMarker } from "@/components/modules/module-nav-marker";
import { ALL_NAV_ITEMS, BOTTOM_NAV, isNavItemActive } from "@/components/navigation/nav-config";
import {
	type PresentedNavItem,
	usePresentedNavItems,
} from "@/components/navigation/use-nav-availability";
import { usePrefersReducedMotion } from "@/hooks/use-prefers-reduced-motion";
import { cn } from "@/lib/cn";
import { useAddMenuStore } from "@/stores/add-menu";

const ITEM_CLASS =
	"relative flex flex-col items-center justify-center gap-1 flex-1 min-h-[48px] rounded-[11px] cursor-pointer mx-0.5 transition-[background-color,color,transform] duration-fast active:scale-95";
const ITEM_ACTIVE =
	"text-primary [background-image:linear-gradient(180deg,var(--surface),var(--surface-2))] shadow-[0_2px_0_var(--line-2),inset_0_1px_0_var(--sh-hl-top)]";
const ITEM_IDLE = "text-muted-foreground active:bg-muted/40";

interface BottomNavProps {
	/** Открыть drawer со всеми разделами (пункт «Ещё»). */
	onOpenMore: () => void;
}

export const BottomNav = forwardRef<HTMLElement, BottomNavProps>(function BottomNav(
	{ onOpenMore },
	ref,
) {
	const { t } = useTranslation("layout");
	const openAddMenu = useAddMenuStore((s) => s.open);
	const pathname = useRouterState({ select: (s) => s.location.pathname });
	// «Ещё» подсвечивается, когда активного основного пункта нет (юзер в одном
	// из разделов, доступных через drawer: тесты/карточки/записи/настройки/...).
	// Нижняя навигация читает ту же карту доступности, что сайдбар и шторка:
	// раздел, выключенный для этого пользователя, не должен оставаться закреплённым
	// внизу экрана только потому, что поверхность другая.
	const bottomItems = usePresentedNavItems(BOTTOM_NAV);
	const anyPrimaryActive = bottomItems.some((item) =>
		isNavItemActive(pathname, item, ALL_NAV_ITEMS),
	);

	return (
		<nav
			ref={ref}
			aria-label={t("sidebar.ariaLabel")}
			// Отдельный слой перехода — по той же причине, что и шапка
			// (components/layout/topbar.tsx): иначе навигация попадает в снимок
			// содержимого и двоится с нижним баром экрана курса.
			style={{ viewTransitionName: "sh-bottomnav" }}
			className={cn(
				"fixed bottom-0 left-0 right-0 z-30 lg:hidden",
				// Панель оболочки: surface чуть выше фона + верхнее «ребро» и блик.
				"border-t border-border bg-card",
				"shadow-[0_-1px_0_var(--line-2),0_-8px_20px_rgb(var(--shadow)/0.06)]",
				"pb-[env(safe-area-inset-bottom)]",
			)}
		>
			<div className="h-16 flex items-stretch justify-around px-1.5 pt-1.5 pb-1">
				{bottomItems.slice(0, 2).map((item) => (
					<NavTab key={item.to} item={item} pathname={pathname} t={t} />
				))}
				{/* Центр — синяя кнопка «+»: открывает попап «что добавить»
				    (загрузить материал / записать лекцию). Это action, а не
				    маршрут, поэтому стоит особняком, объёмной add-tile-плиткой. */}
				<button
					type="button"
					onClick={openAddMenu}
					className={cn(
						"relative flex flex-1 flex-col items-center justify-center",
						"mx-0.5 cursor-pointer transition-transform duration-fast active:scale-95",
					)}
					aria-label={t("topbar.create")}
				>
					<span className="add-tile inline-flex size-12 items-center justify-center rounded-[15px]">
						<IconPlus size={24} stroke={2.5} />
					</span>
				</button>
				{bottomItems.slice(2).map((item) => (
					<NavTab key={item.to} item={item} pathname={pathname} t={t} />
				))}
				{/* «Ещё» — открывает drawer со всеми разделами (паритет с sidebar). */}
				<button
					type="button"
					onClick={onOpenMore}
					className={cn(ITEM_CLASS, anyPrimaryActive ? ITEM_IDLE : ITEM_ACTIVE)}
					aria-label={t("nav.more")}
				>
					<IconLayoutGrid size={22} stroke={1.75} />
					<span className="w-full truncate px-0.5 text-center text-2xs font-medium leading-none">
						{t("nav.more")}
					</span>
				</button>
			</div>
		</nav>
	);
});

// Одна вкладка-раздел нижней навигации (приподнятый чип при активности).
function NavTab({
	item,
	pathname,
	t,
}: {
	item: PresentedNavItem;
	pathname: string;
	t: (key: string) => string;
}) {
	const Icon = item.icon;
	const isActive = isNavItemActive(pathname, item, ALL_NAV_ITEMS);
	const label = t(item.shortLabelKey ?? item.labelKey);
	const haptic = useHaptic();
	// Уменьшенное движение — просьба не дёргать человека вообще, не только не анимировать.
	const reduceMotion = usePrefersReducedMotion();
	return (
		<Link
			to={item.to}
			preload="viewport"
			// Отдача на смену вкладки — `selection`, а не `impact`: это выбор из
			// ряда, а не завершённое действие. Ровно так же ведут себя нативные
			// сегментные контролы. Переключение раздела здесь мгновенное (переход
			// не запускается вовсе), поэтому вибрация — единственное, что
			// подтверждает нажатие помимо смены подсветки.
			onPointerDown={() => {
				if (!reduceMotion && !isActive) haptic.selection();
			}}
			// Признака `viewTransition` здесь нет намеренно — как и на всех прочих
			// ссылках приложения. Значение с самой ссылки перебивает роутерное
			// умолчание и в JSX-шорткате равно `true`: правило направления тогда
			// не спрашивают вовсе, и переход запускается даже там, где мы просили
			// его не запускать (переключение раздела). Решает одно место —
			// lib/nav-direction.ts.
			className={cn(
				ITEM_CLASS,
				isActive ? ITEM_ACTIVE : ITEM_IDLE,
				isDimmedPresentation(item.presentation) && "opacity-60",
			)}
			aria-current={isActive ? "page" : undefined}
			aria-label={t(item.labelKey)}
		>
			{isActive && (
				<span
					aria-hidden="true"
					className="absolute top-0 left-1/2 -translate-x-1/2 h-[3px] w-7 rounded-b-[3px] bg-primary"
				/>
			)}
			{/* Знак состояния — в углу вкладки: подписи внизу узкие, встроить его
			    в строку названия негде, а само название важнее знака. */}
			<span className="absolute right-1.5 top-1">
				<ModuleNavMarker presentation={item.presentation} size={10} />
			</span>
			<Icon size={22} stroke={1.75} />
			<span className="w-full truncate px-0.5 text-center text-2xs font-medium leading-none">
				{label}
			</span>
		</Link>
	);
}
